/**
 * The email step (sign-in, sign-up and password reset all start with it),
 * sign-up's details and the reset. The same fields on both web portals.
 * Messages are locale keys; the screen translates them.
 */

import { z } from 'zod';

import { passwordSchema } from './password';

const email = z
  .string()
  .trim()
  .min(1, 'validation.email.required')
  .pipe(z.email('validation.email.invalid'));

/** "What's your email?" — the first question of sign-in, sign-up and reset. */
export const emailStepSchema = z.object({ email });

export type EmailStepInput = z.infer<typeof emailStepSchema>;

/**
 * Sign-up, step 2: who they are and a password. No confirmation field — the
 * password field can show what was typed.
 */
export const signUpDetailsSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, 'validation.firstName.required')
    .max(50, 'validation.firstName.tooLong'),
  lastName: z
    .string()
    .trim()
    .min(1, 'validation.lastName.required')
    .max(50, 'validation.lastName.tooLong'),
  password: passwordSchema,
});

export type SignUpDetailsInput = z.infer<typeof signUpDetailsSchema>;

/** Reset, step 2: the emailed 6-digit code and the new password. */
export const resetPasswordSchema = z
  .object({
    code: z
      .string()
      .trim()
      .regex(/^\d{6}$/, 'validation.code.sixDigits'),
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'validation.password.confirmRequired'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'validation.password.mismatch',
    path: ['confirmPassword'],
  });

export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
